import { memo, useCallback, useMemo } from 'react'
import { Input } from '@shared/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { Label } from '@shared/components/ui/label'
import { INPUTS, COMPOUND_INPUT, SELECTS, TYPOGRAPHY } from './styles'
import { cn } from '@shared/lib/utils'

interface BillingIntervalInputProps {
  interval: string
  period: string
  onIntervalChange: (interval: string) => void
  onPeriodChange: (period: string) => void
  ariaLabel?: string
  label?: string
  description?: string
  className?: string
}

const PERIOD_OPTIONS = [
  { value: 'day', label: 'day' },
  { value: 'week', label: 'week' },
  { value: 'month', label: 'month' },
  { value: 'year', label: 'year' }
] as const

export const BillingIntervalInput = memo<BillingIntervalInputProps>(function BillingIntervalInput({
  interval,
  period,
  onIntervalChange,
  onPeriodChange,
  ariaLabel = 'Billing interval',
  label,
  description,
  className,
}) {
  const handleIntervalChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onIntervalChange(e.target.value)
  }, [onIntervalChange])

  const summaryText = useMemo(() => {
    const num = parseInt(interval) || 1
    const pluralPeriod = num === 1 ? period : `${period}s`
    return `Every ${num} ${pluralPeriod}`
  }, [interval, period])

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && <Label className={TYPOGRAPHY.label}>{label}</Label>}
      <div className={COMPOUND_INPUT.wrapper} role="group" aria-label={ariaLabel}>
        <div className="flex-1 relative">
          <Input
            type="number"
            placeholder="1"
            value={interval}
            onChange={handleIntervalChange}
            className={cn(INPUTS.quantity, COMPOUND_INPUT.inputSection, '[border-top-left-radius:10px] [border-bottom-left-radius:10px]')}
            min="1"
            aria-label="Billing interval number"
          />
        </div>
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger
            className={cn(SELECTS.currency, COMPOUND_INPUT.selectSection)}
            aria-label="Billing period"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={SELECTS.currencyContent}>
            {PERIOD_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {description ? (
        <p className="text-xs text-muted-foreground font-light leading-tight">{description}</p>
      ) : (
        <p className="text-xs text-muted-foreground/60 font-light leading-tight">{summaryText}</p>
      )}
    </div>
  )
})

BillingIntervalInput.displayName = 'BillingIntervalInput'
